import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, ArrowRight } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { KM, BUYER_GUIDE } from "../../data/km";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FORM_RELAY = "https://formsubmit.co/ajax/kristin.mcintyre@exprealty.com";
const field = "w-full bg-transparent border-b border-black/20 py-3 text-base sm:text-sm outline-none focus:border-black transition-colors placeholder:text-black/40";
const empty = { name: "", email: "", phone: "", consent: false };

export const GuideCover = ({ className = "" }) => (
  <div className={`relative aspect-[935/1210] shadow-[0_30px_60px_-25px_rgba(0,0,0,0.45)] overflow-hidden bg-white ${className}`}>
    <img src={BUYER_GUIDE.cover} alt="RealHer State Buyer's Blueprint, Dallas–Fort Worth 2026 Edition, by Kristin McIntyre" className="absolute inset-0 w-full h-full object-cover km-zoom" />
    <span className="absolute left-0 inset-y-0 w-[5px] bg-black/10" aria-hidden="true" />
  </div>
);

const BLUEPRINT = { name: "Buyer's Blueprint", url: BUYER_GUIDE.url, cta: "Get My Blueprint", open: "Open Your Blueprint" };

export function GuideForm({ item = BLUEPRINT, title = "Get your free copy" }) {
  const [f, setF] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !/^\S+@\S+\.\S+$/.test(f.email)) return toast.error("Please add your name and a valid email.");
    if (!f.consent) return toast.error("Please agree to be contacted.");
    setSending(true);
    try {
      const res = await fetch(FORM_RELAY, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: `${item.name} download: ${f.name}`,
          _template: "table",
          _captcha: "false",
          _replyto: f.email,
          Name: f.name,
          Email: f.email,
          Phone: f.phone || "-",
          Request: `Downloaded the ${item.name}`,
        }),
      });
      if (!res.ok) throw new Error("relay");
      axios.post(`${API}/leads`, { ...f, interest: item.name, message: `${item.name} download` }).catch(() => {});
      setDone(true);
      setF(empty);
    } catch {
      toast.error(`Something went wrong. Please try again or call ${KM.phone}.`);
    } finally {
      setSending(false);
    }
  };

  if (done) return (
    <div data-testid="guide-success">
      <p className="km-serif italic text-[26px] text-[#2C4A3B] mb-3">It’s all yours.</p>
      <p className="km-body text-[#141414] mb-8">Enjoy the read, and reach out anytime you’re ready to talk next steps.</p>
      <a href={item.url} target="_blank" rel="noopener noreferrer" className="km-btn-dark gap-2 w-full sm:w-auto" data-testid="guide-open"><Download size={14} /> {item.open}</a>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-2" data-testid="guide-form">
      <p className="km-serif text-[26px] sm:text-[30px] leading-tight text-[#141414]">{title}</p>
      <p className="km-body text-[#141414] !text-[14px] mb-2">Tell me where to send it and it’s yours, instantly.</p>
      <input className={field} placeholder="Name" value={f.name} onChange={set("name")} data-testid="guide-name" />
      <input className={field} placeholder="Email" value={f.email} onChange={set("email")} data-testid="guide-email" />
      <input className={field} placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} data-testid="guide-phone" />
      <label className="flex gap-3 items-start text-[11px] leading-relaxed text-[#141414] cursor-pointer pt-3">
        <Checkbox checked={f.consent} onCheckedChange={set("consent")} data-testid="guide-consent" />
        <span>I agree to be contacted by {KM.name} via call, email, and text for real estate services. Reply STOP to opt out. Message and data rates may apply.</span>
      </label>
      <div className="pt-4"><button type="submit" disabled={sending} className="km-btn-dark w-full sm:w-auto disabled:opacity-60" data-testid="guide-submit">{sending ? "Sending..." : item.cta}</button></div>
    </form>
  );
}

export default function BuyerGuideTeaser() {
  return (
    <section id="buyers-guide" className="py-12 md:py-24 px-6 md:px-12 lg:px-16 bg-[#F4F3EF] scroll-mt-[84px]" data-testid="km-buyer-guide">
      <div className="reveal max-w-[980px] mx-auto flex items-center gap-6 sm:gap-10 md:gap-16">
        <Link to="/buyers-blueprint" className="w-[34%] max-w-[260px] shrink-0" aria-label="View the free Buyer's Blueprint" data-testid="guide-cover"><GuideCover /></Link>
        <div className="min-w-0">
          <p className="km-eyebrow text-[10px] lg:text-[12.5px] mb-3 md:mb-5 text-[#2C4A3B]">Complimentary</p>
          <h2 className="km-serif text-[30px] sm:text-5xl lg:text-[60px] leading-[1.05] text-[#141414] mb-3 md:mb-5">The Buyer’s <em>Blueprint</em></h2>
          <p className="km-serif italic text-[17px] sm:text-[22px] lg:text-[26px] leading-[1.3] text-[#141414] mb-5 md:mb-8">Everything you need before you start touring.</p>
          <Link to="/buyers-blueprint" className="km-btn-dark gap-1.5 !min-w-0 !px-3.5 sm:!px-8 max-[400px]:!text-[10px]" data-testid="guide-cta">Get the Blueprint <ArrowRight size={14} strokeWidth={1.5} /></Link>
        </div>
      </div>
    </section>
  );
}
